import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';

import { LOGIN_PATH } from '../config/routes';
import { useAuth } from '../contexts/AuthContext';
import { useWebsite, setFlashMessage } from '../contexts/WebsiteContext';
import config from '../config/config';
import NavigationBar from '../components/NavigationBar';
import MediaList from '../components/MediaList';
import Menus from '../components/Menus';
import Footer from '../components/Footer/Footer';
import TicketList from './../components/Tickets/TicketList';
import TicketDetailsModal from './../components/Tickets/TicketDetailsModal';
import CreateTicketModal from './../components/Tickets/CreateTicketModal';
import ConfirmationModal from './../components/Tickets/ConfirmationModal';

function Tickets() {
    const { website, setIsLoading } = useWebsite();
    const { token, isAuthLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthLoaded && !token) {
            navigate(LOGIN_PATH);
        }
    }, [token, navigate, isAuthLoaded]);

    const [tickets, setTickets] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState('');
    const [selectedActionTicket, setSelectedActionTicket] = useState(null);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showFullScreen, setShowFullScreen] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [formData, setFormData] = useState({
        ticketType: 'HELP',
        subject: '',
        description: '',
        file: null,
        website: website,
    });

    const fetchTickets = async () => {
        try {
            const response = await axios.get(`${config.apiUrl}ticket?website=${website}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(response.data);
        } catch (error) {
            console.error('Error fetching tickets:', error);
        }
    };

    useEffect(() => {
        if (token) {
            fetchTickets();
        }
    }, [token, website]);

    const handleOpenCreateModal = () => setShowCreateModal(true);
    const handleCloseCreateModal = () => setShowCreateModal(false);

    const handleOpenDetailsModal = (ticket) => {
        setSelectedTicket(ticket);
        setShowDetailsModal(true);
    };
    const handleCloseDetailsModal = () => setShowDetailsModal(false);

    const handleOpenConfirmationModal = (action, ticket) => {
        setConfirmationAction(action);
        setSelectedActionTicket(ticket);
        setShowConfirmationModal(true);
    };
    const handleCloseConfirmationModal = () => setShowConfirmationModal(false);

    const handleConfirmAction = async () => {
        setIsLoading(true);
        try {
            await axios.put(`${config.apiUrl}ticket/${selectedActionTicket.id}`, {
                status: confirmationAction,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchTickets();
            setFlashMessage(`La demande n°${selectedActionTicket.id} a été ${confirmationAction === 'CLOSED' ? 'clôturée' : 'archivée'} avec succès.`, 'success');
        } catch (error) {
            setFlashMessage(`Une erreur s'est produite lors de ${confirmationAction === 'CLOSED' ? "la clôture" : "l'archivage"} de la demande.`, 'error');
            console.error(`Error ${confirmationAction === 'CLOSED' ? 'closing' : 'archiving'} ticket:`, error);
        }
        setShowConfirmationModal(false);
        setIsLoading(false);
    };

    const handleCreateTicket = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            const formDataWithFile = new FormData();
            for (let key in formData) {
                formDataWithFile.append(key, formData[key]);
            }
            await axios.post(`${config.apiUrl}ticket`, formDataWithFile, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            fetchTickets();
            setShowCreateModal(false);
            setFlashMessage('La nouvelle demande a été créée avec succès.', 'success');
        } catch (error) {
            setFlashMessage('Une erreur s\'est produite lors de la création de la demande.', 'error');
            console.error('Error creating ticket:', error);
        }
        setIsLoading(false);
    };

    const getTicketStatusLabel = (status) => {
        switch (status?.toUpperCase()) {
            case 'OPEN': return 'Ouvert';
            case 'CANCELED': return 'Annulée';
            case 'IN_PROGRESS': return 'En cours d\'étude';
            case 'AWAITING_FOR_CUSTOMER': return 'En attente de votre réponse';
            case 'CLOSED': return 'Terminée';
            case 'ARCHIVED': return 'Archivée';
            default: return '';
        }
    };

    const getTicketTypeLabel = (type) => {
        switch (type?.toUpperCase()) {
            case 'HELP': return 'Assistance';
            case 'ANOMALY': return 'Anomalie/Bug';
            default: return '';
        }
    };

    const getTicketImageUrl = (ticket) => `${config.documentsUrl}tickets/${ticket.uuid}.png`;

    const getKubuniAnswer = (ticket) => {
        if (ticket.answer) return ticket.answer;
        switch (ticket.status) {
            case 'OPEN': return 'La demande sera bientôt prise en charge.';
            case 'IN_PROGRESS': return 'La demande est en cours d\'étude.';
            case 'AWAITING_FOR_CUSTOMER': return 'La demande est en attente de votre retour.';
            case 'CANCELED': return 'La demande a été annulée.';
            case 'CLOSED': return 'La demande a été traitée.';
            case 'ARCHIVED': return 'La demande a été traitée puis archivée.';
            default: return '';
        }
    };

    return (
        <div>
            <NavigationBar />
            <h1>Mes demandes</h1>
            <Button variant="primary" onClick={handleOpenCreateModal} style={{ marginBottom: '20px' }}>
                Créer une nouvelle demande
            </Button>
            <TicketList
                tickets={tickets}
                showArchived={showArchived}
                setShowArchived={setShowArchived}
                getTicketTypeLabel={getTicketTypeLabel}
                getTicketStatusLabel={getTicketStatusLabel}
                handleOpenDetailsModal={handleOpenDetailsModal}
                handleOpenConfirmationModal={handleOpenConfirmationModal}
            />
            <MediaList mediaKey='TICKETS_BOTTOM' maxItems={1} isShortLine={true} />
            <Menus />
            <Footer />

            {selectedTicket && (
                <TicketDetailsModal
                    show={showDetailsModal}
                    onHide={handleCloseDetailsModal}
                    ticket={selectedTicket}
                    getTicketTypeLabel={getTicketTypeLabel}
                    getTicketStatusLabel={getTicketStatusLabel}
                    getKubuniAnswer={getKubuniAnswer}
                    getTicketImageUrl={getTicketImageUrl}
                    showFullScreen={showFullScreen}
                    setShowFullScreen={setShowFullScreen}
                    token={token}
                />
            )}

            <CreateTicketModal
                show={showCreateModal}
                onHide={handleCloseCreateModal}
                formData={formData}
                setFormData={setFormData}
                handleCreateTicket={handleCreateTicket}
                getTicketTypeLabel={getTicketTypeLabel}
            />

            {selectedActionTicket && (
                <ConfirmationModal
                    show={showConfirmationModal}
                    onHide={handleCloseConfirmationModal}
                    action={confirmationAction}
                    ticket={selectedActionTicket}
                    handleConfirmAction={handleConfirmAction}
                />
            )}
        </div>
    );
}

export default Tickets;